import { useCallback, useRef } from 'react';
import { calculateNextNodePosition } from '../../../../utils/nodePositioning';
import { isMilestoneNode } from '../../FlowchartView/utils/milestoneUtils';
import { isNoteNode, isWorkNode, suggestNotePosition } from '../../FlowchartView/utils/noteUtils';
import {
  resolveMilestonePlacement,
  buildNodePositionsFromTasks,
  getMilestoneJunctionPositionOnMidpoint,
  resolveOutputMilestoneHostFromEdge,
} from '../../FlowchartView/utils/milestonePlacement';
import { scheduleNoteAddLayoutFollow } from './scheduleNoteAddLayoutFollow';
import { scheduleParallelMissionLayout } from './scheduleParallelMissionLayout';
import { buildParallelMission } from '../../FlowchartView/utils/parallelMission';
import { buildMissionBefore } from '../../FlowchartView/utils/insertMissionBefore';
import { generateTaskId } from '../taskActions/utils';
import { BOARD_PAN_TO } from '../../BoardView/events';

/**
 * useNodeCreation Hook
 * 
 * Handles adding new nodes (tasks/decisions) to the flowchart.
 * Places new nodes to the right of all existing nodes for consistency.
 * Supports multi-campaign mode with smart positioning.
 * 
 * @param {Object} options
 * @param {Object} options.localSprint - The current sprint data
 * @param {Function} options.setLocalSprint - Setter for sprint data
 * @param {Object} options.flowchartRef - Ref to flowchart component
 * @param {Function} options.recordAction - Function to record undo/redo actions
 * @param {Function} options.getVisibleTasks - Optional callback to get visible/filtered tasks
 *                                             When provided, new nodes are positioned relative to visible tasks only
 */
export const useNodeCreation = ({
  localSprint,
  setLocalSprint,
  flowchartRef,
  recordAction,
  getVisibleTasks,
  ensureFlowView,
}) => {
  const localSprintRef = useRef(localSprint);
  localSprintRef.current = localSprint;
  const ensureFlowViewRef = useRef(ensureFlowView);
  ensureFlowViewRef.current = ensureFlowView;
  const getLayoutContext = useCallback(() => {
    const tasks = localSprint?.tasks || [];
    const displayPositions = flowchartRef.current?.getNodePositions?.() ?? {};
    const nodeDimensions = flowchartRef.current?.getNodeDimensions?.() ?? {};
    const positions = buildNodePositionsFromTasks(tasks, displayPositions);
    return { tasks, positions, nodeDimensions };
  }, [localSprint?.tasks, flowchartRef]);

  // Handle adding a node
  // options.sourceCampaign can be provided in multi-campaign mode
  // options.allTasks can be provided for multi-campaign positioning (needed to calculate campaign zones)
  const handleAddNodeAtViewport = useCallback((nodeType = 'task', options = {}) => {
    const { sourceCampaign, allTasks, milestoneAnchor, explicitPosition: optPosition, initialName, taskId: optTaskId } = options;
    const taskId = optTaskId || `task-${Date.now()}`;
    const now = new Date().toISOString();
    
    // Determine position based on mode
    let position;
    let intraCampaignPosition; // Position within the campaign's own coordinate system
    
    if (optPosition) {
      position = optPosition;
      intraCampaignPosition = optPosition;
    } else if (sourceCampaign && allTasks && flowchartRef.current?.findPositionInCampaign) {
      // Multi-campaign mode: use smart positioning within campaign zone
      // If campaign is visible, finds empty space in viewport
      // If campaign is not visible, places to the right of rightmost top-level node
      const result = flowchartRef.current.findPositionInCampaign(sourceCampaign.id, allTasks);
      position = result.position;
      
      // Calculate the campaign offset by looking at existing tasks in this campaign
      // The offset is stored on tasks as _campaignOffset when loaded by multiCampaignDataLoader
      const existingCampaignTask = allTasks.find(t => 
        t.sourceCampaign?.id === sourceCampaign.id && t._campaignOffset !== undefined
      );
      const campaignOffset = existingCampaignTask?._campaignOffset || 0;
      
      // Calculate intra-campaign position by subtracting the offset
      // This is the position that will be saved back to the source campaign
      intraCampaignPosition = {
        x: position.x - campaignOffset,
        y: position.y,
      };
      
      // Apply campaign adjustments if any (shift other campaigns to the right)
      if (result.campaignAdjustments) {
        Object.values(result.campaignAdjustments).forEach((adjustment) => {
          // Apply pre-calculated position shifts for each task
          if (adjustment.positionShifts) {
            Object.entries(adjustment.positionShifts).forEach(([shiftTaskId, newPos]) => {
              flowchartRef.current?.setTaskPosition(shiftTaskId, newPos);
            });
          }
        });
      }
    } else {
      // Normal mode: place to the right of all existing nodes
      // Use visible/filtered tasks if available, otherwise fall back to all tasks
      // This ensures new nodes are positioned relative to what the user can see
      const tasksForPositioning = getVisibleTasks?.() || localSprint?.tasks;
      position = optPosition || calculateNextNodePosition(tasksForPositioning);
      intraCampaignPosition = position; // Same in single-campaign mode
    }
    
    flowchartRef.current?.setTaskPosition(taskId, position);
    
    const baseTask = {
      id: taskId,
      name: nodeType === 'milestone' ? 'Milestone' : nodeType === 'wait' ? 'Wait for e-mail' : '',
      order: (localSprint?.tasks?.length || 0) + 1,
      groupId: null,
      dependsOn: [],
      status: 'pending',
      // Store intra-campaign position for saving back to source campaign
      x: intraCampaignPosition.x,
      y: intraCampaignPosition.y,
      // Date fields
      createdAt: now,
      updatedAt: now,
      startedAt: null,
      completedAt: null,
      estimatedDurationMinutes: null,
      dueDate: null,
    };

    // Add decision-specific properties
    if (nodeType === 'decision') {
      baseTask.nodeType = 'decision';
      baseTask.options = [
        { id: 'yes', label: 'Yes', color: '#22c55e' },
        { id: 'no', label: 'No', color: '#ef4444' },
      ];
      baseTask.answer = null;
    } else if (nodeType === 'wait') {
      baseTask.nodeType = 'wait';
      baseTask.waitFor = 'email';
      baseTask.waitDetails = '';
      baseTask.fulfilledAt = null;
    } else if (nodeType === 'milestone') {
      baseTask.nodeType = 'milestone';
      if (milestoneAnchor) {
        baseTask.milestoneAnchor = milestoneAnchor;
      }
    } else if (nodeType === 'note') {
      baseTask.nodeType = 'note';
      if (options.noteAnchor) {
        baseTask.noteAnchor = options.noteAnchor;
      }
    }

    if (initialName) {
      baseTask.name = initialName;
    }

    // Add multi-campaign mode metadata
    if (sourceCampaign) {
      baseTask.sourceCampaign = sourceCampaign;
      // Store view-specific metadata for proper display and sync
      baseTask.position = position;
      baseTask._originalPosition = { x: intraCampaignPosition.x, y: intraCampaignPosition.y };
      // Get offset from existing task or default to 0
      const existingCampaignTask = allTasks?.find(t => 
        t.sourceCampaign?.id === sourceCampaign.id && t._campaignOffset !== undefined
      );
      baseTask._campaignOffset = existingCampaignTask?._campaignOffset || 0;
    }

    const commitTask = () => {
      setLocalSprint(prev => {
        if (!prev) return prev;
        return { ...prev, tasks: [...(prev.tasks || []), baseTask] };
      });
    };

    // Right-click "create here" already placed the card under the cursor — don't
    // recenter the viewport or the whole board appears to slide.
    if (!optPosition && flowchartRef.current?.panToPosition) {
      commitTask();
      flowchartRef.current.panToPosition(position, { adjustZoom: true });
    } else if (!optPosition) {
      const width = nodeType === 'decision' ? 288 : 320;
      const height = nodeType === 'decision' ? 140 : 162;
      window.dispatchEvent(new CustomEvent(BOARD_PAN_TO, {
        detail: { x: position.x, y: position.y, width, height, adjustZoom: true, taskId },
      }));
      commitTask();
    } else {
      commitTask();
    }

    // Record for undo
    if (recordAction) {
      recordAction({
        type: 'ADD_TASK',
        description: nodeType === 'decision' ? 'Add Decision' : nodeType === 'wait' ? 'Add Wait for' : nodeType === 'milestone' ? 'Add Milestone' : nodeType === 'note' ? 'Add Note' : 'Add Mission',
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return { ...prev, tasks: prev.tasks.filter(t => t.id !== taskId) };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return { ...prev, tasks: [...prev.tasks, baseTask] };
          });
        },
      });
    }

    // Isolated "drop here" nodes stay where they were spawned. Tidy would treat
    // them as a new chain root and park them at the top of the board.
    const joinsExistingGraph = Boolean(milestoneAnchor);
    if (joinsExistingGraph) {
      setTimeout(() => {
        flowchartRef.current?.autoLayout?.(new Set());
      }, 50);
    }

    return taskId;
  }, [setLocalSprint, flowchartRef, localSprint?.tasks, recordAction, getVisibleTasks]);

  const addMilestoneAttachedToHost = useCallback(
    (hostTaskId, side, { positionOverride } = {}) => {
      const { tasks, positions, nodeDimensions } = getLayoutContext();
      const host = tasks.find((t) => t.id === hostTaskId);
      if (!host || isMilestoneNode(host)) return null;

      const existing = tasks.filter(
        (t) => isMilestoneNode(t)
          && t.milestoneAnchor?.hostTaskId === hostTaskId
          && t.milestoneAnchor?.side === side,
      );

      const { position } = resolveMilestonePlacement({
        hostTask: host,
        side,
        tasks,
        positions,
        nodeDimensions,
        existingSameSide: existing,
        positionOverride,
      });

      const label = side === 'output' ? 'Complete checkpoint' : 'Ready checkpoint';
      const taskId = `task-${Date.now()}`;

      handleAddNodeAtViewport('milestone', {
        taskId,
        milestoneAnchor: { hostTaskId, side },
        explicitPosition: position,
        initialName: label,
      });

      return taskId;
    },
    [getLayoutContext, handleAddNodeAtViewport],
  );

  // Convenience wrappers - accept optional options object for multi-campaign mode
  const handleAddTaskAtViewport = useCallback(
    (options = {}) => handleAddNodeAtViewport('task', options), 
    [handleAddNodeAtViewport]
  );
  
  const handleAddDecisionAtViewport = useCallback(
    (options = {}) => handleAddNodeAtViewport('decision', options), 
    [handleAddNodeAtViewport]
  );

  const handleAddWaitAtViewport = useCallback(
    (options = {}) => handleAddNodeAtViewport('wait', options),
    [handleAddNodeAtViewport]
  );

  const handleAddMilestoneOnHost = useCallback(
    (hostTaskId, side = 'input') => addMilestoneAttachedToHost(hostTaskId, side),
    [addMilestoneAttachedToHost],
  );

  const handleAddNoteOnHost = useCallback(
    (hostTaskId) => {
      const { tasks, positions, nodeDimensions } = getLayoutContext();
      const host = tasks.find((t) => t.id === hostTaskId);
      if (!host || !isWorkNode(host)) return null;

      const existing = tasks.filter(
        (t) => isNoteNode(t) && t.noteAnchor?.hostTaskId === hostTaskId,
      );
      const position = suggestNotePosition(host, positions, nodeDimensions, existing);
      const taskId = `task-${Date.now()}`;

      handleAddNodeAtViewport('note', {
        taskId,
        noteAnchor: { hostTaskId },
        explicitPosition: position,
      });

      scheduleNoteAddLayoutFollow(flowchartRef, hostTaskId);

      return taskId;
    },
    [getLayoutContext, handleAddNodeAtViewport, flowchartRef],
  );

  /** Insert passive milestone on a dependency line — anchors to edge.from mission (when complete). */
  const handleInsertMilestoneOnEdge = useCallback(
    (edge, midpoint) => {
      const { tasks } = getLayoutContext();
      const hostTaskId = resolveOutputMilestoneHostFromEdge(edge, tasks);
      if (!hostTaskId) return null;

      const positionOverride = getMilestoneJunctionPositionOnMidpoint(midpoint);
      return addMilestoneAttachedToHost(hostTaskId, 'output', { positionOverride });
    },
    [getLayoutContext, addMilestoneAttachedToHost],
  );

  const commitDerivedMission = useCallback((sourceTaskId, build, action) => {
    const sprint = localSprintRef.current;
    if (!sprint || !sourceTaskId) return null;

    const newTaskId = generateTaskId();
    const now = new Date().toISOString();
    const built = build(sprint.tasks, sourceTaskId, { newTaskId, now });
    if (!built) return null;

    const previousTasks = sprint.tasks;
    setLocalSprint((prev) => (prev ? { ...prev, tasks: built.tasks } : prev));
    flowchartRef.current?.setTaskPosition?.(newTaskId, built.displayPosition);

    const layout = () => scheduleParallelMissionLayout(flowchartRef, newTaskId, {
      ensureFlowView: () => ensureFlowViewRef.current?.(),
    });

    if (recordAction) {
      const nextTasks = built.tasks;
      recordAction({
        type: action.type,
        description: action.description,
        undo: () => {
          setLocalSprint((prev) => (prev ? { ...prev, tasks: previousTasks } : prev));
        },
        redo: () => {
          setLocalSprint((prev) => (prev ? { ...prev, tasks: nextTasks } : prev));
          layout();
        },
      });
    }

    layout();
    return newTaskId;
  }, [setLocalSprint, flowchartRef, recordAction]);

  const handleCreateParallelMission = useCallback((sourceTaskId) => (
    commitDerivedMission(sourceTaskId, buildParallelMission, {
      type: 'CREATE_PARALLEL_MISSION',
      description: 'Create parallel mission',
    })
  ), [commitDerivedMission]);

  const handleInsertMissionBefore = useCallback((sourceTaskId, nodeType = 'task', position = null) => {
    const kind = nodeType === 'decision' ? 'decision' : nodeType === 'wait' ? 'wait' : 'task';
    let spawn = position;
    if (!spawn) {
      const live = flowchartRef.current?.getNodePositions?.()?.[sourceTaskId];
      if (live && Number.isFinite(live.x) && Number.isFinite(live.y)) {
        spawn = { x: live.x, y: live.y - 28 };
      }
    }
    return commitDerivedMission(
      sourceTaskId,
      (tasks, sourceId, ctx) => buildMissionBefore(tasks, sourceId, {
        ...ctx,
        nodeType: kind,
        position: spawn,
      }),
      {
        type: 'INSERT_MISSION_BEFORE',
        description: kind === 'decision'
          ? 'Insert decision before'
          : kind === 'wait'
            ? 'Insert wait before'
            : 'Insert mission before',
      },
    );
  }, [commitDerivedMission]);

  return {
    handleAddNodeAtViewport,
    handleAddTaskAtViewport,
    handleAddDecisionAtViewport,
    handleAddWaitAtViewport,
    handleAddMilestoneOnHost,
    handleAddNoteOnHost,
    handleInsertMilestoneOnEdge,
    handleCreateParallelMission,
    handleInsertMissionBefore,
  };
};

export default useNodeCreation;
